import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CountUp } from './CountUp';

type LandingPageProps = {
  onPrimaryCta: () => void;
  onSecondaryCta: () => void;
};

type HeroStats = {
  refreshCadenceMinutes: number;
  filingsThisMonth: number;
  signalsWeekToDate: number;
  totalSignals: number;
  totalBuySignals: number;
  uniqueTickers30d: number;
  coveragePct: number | null;
};

export function LandingPage({ onPrimaryCta, onSecondaryCta }: LandingPageProps) {
  const navigate = useNavigate();
  const fetchedOnceRef = useRef(false);

  const [heroStats, setHeroStats] = useState<HeroStats>({
    refreshCadenceMinutes: 10,
    filingsThisMonth: 0,
    signalsWeekToDate: 0,
    totalSignals: 0,
    totalBuySignals: 0,
    uniqueTickers30d: 0,
    coveragePct: null,
  });

  useEffect(() => {
    const cacheKey = 'public_metrics_cache';
    const cachedRaw = localStorage.getItem(cacheKey);
    if (cachedRaw) {
      try {
        const cached = JSON.parse(cachedRaw);
        if (cached?.ts && Date.now() - cached.ts < 5 * 60 * 1000 && cached.data) {
          setHeroStats((prev) => ({
            refreshCadenceMinutes: cached.data.refresh_cadence_minutes ?? prev.refreshCadenceMinutes,
            filingsThisMonth: cached.data.filings_this_month ?? prev.filingsThisMonth,
            signalsWeekToDate: cached.data.signals_week_to_date ?? prev.signalsWeekToDate,
            totalSignals: cached.data.total_signals ?? prev.totalSignals,
            totalBuySignals: cached.data.total_buy_signals ?? prev.totalBuySignals,
            uniqueTickers30d: cached.data.unique_tickers_30d ?? prev.uniqueTickers30d,
            coveragePct: cached.data.coverage_pct ?? prev.coveragePct,
          }));
        }
      } catch {
        /* ignore cache errors */
      }
    }

    const fetchMetrics = async () => {
      if (fetchedOnceRef.current) return;
      fetchedOnceRef.current = true;
      try {
        const resp = await fetch('/public-metrics', { headers: { Accept: 'application/json' } });
        if (!resp.ok) return;
        const data = await resp.json();
        setHeroStats((prev) => ({
          refreshCadenceMinutes: data.refresh_cadence_minutes ?? prev.refreshCadenceMinutes,
          filingsThisMonth: data.filings_this_month ?? prev.filingsThisMonth,
          signalsWeekToDate: data.signals_week_to_date ?? prev.signalsWeekToDate,
          totalSignals: data.total_signals ?? prev.totalSignals,
          totalBuySignals: data.total_buy_signals ?? prev.totalBuySignals,
          uniqueTickers30d: data.unique_tickers_30d ?? prev.uniqueTickers30d,
          coveragePct: data.coverage_pct ?? prev.coveragePct,
        }));
        localStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data }));
      } catch {
        /* silent fail keeps defaults */
      }
    };

    fetchMetrics();
  }, []);

  return (
    <div className="landing">
      <div className="landing-inner">
        <header className="landing-header">
          <div className="container landing-nav">
            <div className="nav-brand">
              <div className="brand-icon brand-icon-frame">
                <img src="/logo-transparent.png" alt="" />
              </div>
              <div>
                <div className="nav-title">EDGAR Insights</div>
                <strong>Trade the filing tape</strong>
              </div>
            </div>
            <nav aria-label="Primary">
              <ul className="nav-links">
                <li><Link to="/">Home</Link></li>
                <li><Link to="/about">About</Link></li>
                <li><Link to="/features">Features</Link></li>
                <li><Link to="/pricing">Pricing</Link></li>
                <li><Link to="/blog">Blog</Link></li>
                <li><Link to="/faq">FAQ</Link></li>
              </ul>
            </nav>
            <div className="nav-actions">
              <button className="cta-secondary" type="button" onClick={onSecondaryCta}>
                Sign in
              </button>
              <button className="cta-button" type="button" onClick={onPrimaryCta}>
                Get started
              </button>
            </div>
          </div>
        </header>

        <main>
          <section className="hero" id="top">
            <div className="container hero-grid">
              <div className="hero-copy">
                <span className="hero-eyebrow">Live | Filing heatmap</span>
                <h1 className="hero-title">
                  See tomorrow&apos;s market narrative in today&apos;s SEC filings <span>before the open.</span>
                </h1>
                <p className="hero-lede">
                  EDGAR Insights watches every 10-K, 10-Q, 8-K, and S-1 in real time, then translates what changed into
                  confident trade theses your desk can act on.
                </p>
                <div className="hero-actions">
                  <button className="cta-button" type="button" onClick={onPrimaryCta}>
                    Start free
                  </button>
                  <button className="cta-secondary" type="button" onClick={() => navigate('/features')}>
                    View the product
                  </button>
                </div>
                <div className="hero-stats">
                  <div className="hero-stat">
                    <span className="label">Refresh cadence</span>
                    <span className="value">
                      Every <CountUp value={heroStats.refreshCadenceMinutes} /> minutes
                    </span>
                  </div>
                  <div className="hero-stat">
                    <span className="label">Total signals</span>
                    <span className="value"><CountUp value={heroStats.totalSignals} /></span>
                  </div>
                  <div className="hero-stat">
                    <span className="label">Total BUY signals</span>
                    <span className="value"><CountUp value={heroStats.totalBuySignals} /></span>
                  </div>
                  <div className="hero-stat">
                    <span className="label">Signals this week</span>
                    <span className="value"><CountUp value={heroStats.signalsWeekToDate} /></span>
                  </div>
                </div>
              </div>

              <div className="hero-panel" aria-label="Live insight preview">
                <h3>EDGAR signal stream</h3>
                <div className="panel-metric">
                  <span>Latest filing</span>
                  <strong>8-K | NVDA</strong>
                </div>
                <div className="panel-metric">
                  <span>AI read</span>
                  <strong>Positive, guidance language tightened</strong>
                </div>
                <div className="panel-metric">
                  <span>Suggested action</span>
                  <strong>BUY/BULLISH</strong>
                </div>
                <div className="hero-meter" aria-hidden="true">
                  <div className="hero-meter-fill" />
                  <div className="hero-meter-label"> 92%</div>
                </div>
              </div>
            </div>
          </section>

        </main>

        <footer className="landing-footer">
          <div className="container">
            <div className="site-tagline">
              EDGAR Insights | AI-powered trading signals from SEC filings | Built for fundamental and event-driven teams.
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
